import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { z } from "zod";
import { AuthLayout } from "./AuthLayout";
import { useForgotPassword } from "@/api/hooks/auth";
import { isApiError } from "@/api/errors";
import { Field, FormError, FormSuccess } from "@/components/Field";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const schema = z.object({ email: z.string().trim().min(1, "auth.emailRequired").email("auth.emailInvalid") });
type Values = z.infer<typeof schema>;

export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const forgot = useForgotPassword();
  const form = useForm<Values>({ resolver: zodResolver(schema), defaultValues: { email: "" } });
  const { errors } = form.formState;
  const onSubmit = form.handleSubmit((v) => forgot.mutate(v));
  // The API always answers 202; only rate limiting is surfaced, everything else reads as success.
  const rateLimited = isApiError(forgot.error) && forgot.error.status === 429;
  const done = forgot.isSuccess || (forgot.isError && !rateLimited);

  return (
    <AuthLayout title={t("auth.forgotTitle")} intro={t("auth.forgotIntro")}>
      {done ? (
        <FormSuccess message={t("auth.forgotSuccess")} />
      ) : (
        <form noValidate onSubmit={onSubmit} className="flex flex-col gap-5">
          <FormError message={rateLimited ? t("auth.tooMany") : null} />
          <Field label={t("auth.email")} error={errors.email?.message ? t(errors.email.message) : null}>
            {(p) => <Input {...p} type="email" autoComplete="email" {...form.register("email")} />}
          </Field>
          <Button type="submit" className="w-full" disabled={forgot.isPending}>
            {forgot.isPending ? t("auth.sending") : t("auth.sendLink")}
          </Button>
        </form>
      )}
      <p className="mt-6 text-sm">
        <Link to="/login" className="underline">
          {t("auth.backToSignIn")}
        </Link>
      </p>
    </AuthLayout>
  );
}
